import { ref, toRaw } from "vue";
import { isDobValid, isEmailValid, isNPIValid, isPhoneNumberValid, isZipCodeValid } from "@/utils/validators";
import { isValidNdc } from "@/utils";
import { orderInfoService } from "@/services/orderInfoService";

// Must match CreateSubmissionsBatchRequest's per-request cap in the backend (LAM-4841).
export const MAX_ADDITIONAL_MEDICATIONS = 20;

// Chart entries uploaded on this page, so a same-patient prepopulate keeps them (a save does not end the session).
const sessionUploads = new WeakSet();

/** Whether a chart entry was uploaded on this page rather than loaded or prepopulated. */
export const isSessionUpload = (entry) => sessionUploads.has(toRaw(entry));

export function useOrderInfoUpdates(orderInfo) {
  const formButtonClicked = ref(true);

  // Update functions
  function updateChecklistSelection(newChecklistSelection, isDeselect = false) {
    if (isDeselect) {
      newChecklistSelection.forEach((unselectedItem) => {
        orderInfo.value.checklist = orderInfo.value.checklist.filter((item) => item.id !== unselectedItem.id);
      });
    } else {
      // Only keep items that are in the new selection (currently visible/answered in UI)
      // This ensures we don't save questions that are no longer visible or not yet answered
      const newSelectionIds = new Set(newChecklistSelection.map((item) => item.id));

      // Update existing items that are in the new selection
      const updatedChecklist = orderInfo.value.checklist
        .filter((item) => newSelectionIds.has(item.id))
        .map((item) => {
          const newItem = newChecklistSelection.find((newItem) => newItem.id === item.id);
          return newItem || item;
        });

      // Add new items that aren't in the existing checklist
      newChecklistSelection.forEach((selectedItem) => {
        if (!updatedChecklist.find((item) => item.id === selectedItem.id)) {
          updatedChecklist.push(selectedItem);
        }
      });

      orderInfo.value.checklist = updatedChecklist;
    }
  }

  // The payer questions on the screen, which `orderInfoService.saveOrder` turns into one answer pair
  // each. Not a column and not part of `orderInfo`: the save that reads the pairs is the only
  // request that carries them, and it takes them as an argument.
  const declaredPayerQuestionIds = ref(null);

  function updateDeclaredQuestions(questionIds) {
    declaredPayerQuestionIds.value = questionIds;
  }

  function updateBasicPlusChecklistSelection(newChecklistSelection, isDeselect = false) {
    if (isDeselect) {
      newChecklistSelection.forEach((unselectedItem) => {
        orderInfo.value.basicPlusChecklist = orderInfo.value.basicPlusChecklist.filter(
          (item) => item.id !== unselectedItem.id
        );
      });
    } else {
      // Apply every item in the batch: the component emits all answered questions on each change, so
      // rebuilding from the stored checklist per item kept only the last one and dropped the user's edit.
      const updatedChecklist = [...(orderInfo.value.basicPlusChecklist || [])];
      newChecklistSelection.forEach((selectedItem) => {
        const index = updatedChecklist.findIndex((item) => item.id === selectedItem.id);
        if (index === -1) {
          updatedChecklist.push(selectedItem);
        } else {
          updatedChecklist[index] = selectedItem;
        }
      });
      orderInfo.value.basicPlusChecklist = updatedChecklist;
    }
  }

  function updateSignature(signature) {
    orderInfo.value.signature = signature;
  }

  function updatePrimaryDiagnosis({ icd10Code, name }) {
    orderInfo.value.primaryDiagnosisIcd10Code = icd10Code;
    orderInfo.value.primaryDiagnosisName = name;
  }

  function updateOtherDiagnosis({ index, icd10Code, name }) {
    if (!icd10Code || !name) {
      orderInfo.value.secondaryDiagnosis.splice(index, 1);
      orderInfo.value.secondaryDiagnosis.push({
        name: null,
        icd10Code: null,
      });
    } else {
      const insertIndex = orderInfo.value.secondaryDiagnosis.findIndex((item) => !item.name);
      orderInfo.value.secondaryDiagnosis[insertIndex]["icd10Code"] = icd10Code;
      orderInfo.value.secondaryDiagnosis[insertIndex]["name"] = name;
    }
  }

  function addOtherDiagnosis() {
    orderInfo.value.secondaryDiagnosis.push({ icd10Code: null, name: null });
  }

  function updateOrderInfoField(field, value) {
    orderInfo.value[field] = value;
  }

  function rememberRemovedChartUpload(uploadKey) {
    const named = orderInfo.value.removedChartUploadKeys || [];
    orderInfo.value.removedChartUploadKeys = [...named, uploadKey];
  }

  function patientChartIndex(charts, downloadLinkInfo) {
    if (downloadLinkInfo.chartEntry) return charts.indexOf(downloadLinkInfo.chartEntry);
    return downloadLinkInfo.storeVarIndex ?? -1;
  }

  function updateAiReferenceDocuments(documents, removedLinks = []) {
    orderInfo.value.aiReferenceDocuments = documents;
    if (removedLinks.length) {
      orderInfo.value.removedAiReferenceLinks = [...(orderInfo.value.removedAiReferenceLinks || []), ...removedLinks];
    }
  }

  function updateSupportingDocs(downloadLinkInfo) {
    if (downloadLinkInfo.storeVar === "patientChart" && downloadLinkInfo.newEntry) {
      if (!Array.isArray(orderInfo.value.patientChart)) orderInfo.value.patientChart = [];
      const entry = { label: downloadLinkInfo.fileName, links: [downloadLinkInfo.link] };
      sessionUploads.add(entry);
      orderInfo.value.patientChart.push(entry);
      return;
    }
    if (downloadLinkInfo.storeVar === "patientChart") {
      const charts = orderInfo.value[downloadLinkInfo.storeVar];
      const entryIndex = patientChartIndex(charts, downloadLinkInfo);
      const entry = entryIndex === -1 ? undefined : charts[entryIndex];
      if (entry) {
        if (downloadLinkInfo.isDelete) {
          // Remove the specific link
          entry.links = (entry.links || []).filter((link) => link !== downloadLinkInfo.link);
          // Remove the entire document if no links remain
          if (entry.links.length === 0) {
            if (entry.uploadKey) {
              rememberRemovedChartUpload(entry.uploadKey);
            }
            charts.splice(entryIndex, 1);
          }
        } else {
          entry.links = [...(entry.links || []), downloadLinkInfo.link];
          sessionUploads.add(toRaw(entry));
        }
      }
    } else {
      if (downloadLinkInfo.isDelete) {
        // Remove link from array
        orderInfo.value[downloadLinkInfo.storeVar] = orderInfo.value[downloadLinkInfo.storeVar].filter(
          (link) => link !== downloadLinkInfo.link
        );
      } else {
        orderInfo.value[downloadLinkInfo.storeVar]
          ? orderInfo.value[downloadLinkInfo.storeVar].push(downloadLinkInfo.link)
          : (orderInfo.value[downloadLinkInfo.storeVar] = [downloadLinkInfo.link]);
      }
    }
  }

  function addExtraMedication() {
    orderInfo.value.extraMedications.push({
      medication: "",
      expectedLengthOfTherapy: "",
      quantityPer30Days: "",
      packageSizeUnit: "",
      frequency: "",
      dailyDosage: "",
      nationalDrugCode: "",
      prescriptionNumber: "",
      // Date fields use null (not "") so they don't propagate as raw empty strings
      // into orderInfo.treatmentStartDate via removePrimaryMedication() below, which
      // the save-order endpoint now rejects with 400 (LAM-3863).
      treatmentStartDate: null,
    });
  }

  function removeExtraMedication(index) {
    orderInfo.value.extraMedications.splice(index, 1);
  }

  function removePrimaryMedication() {
    if (orderInfo.value.extraMedications.length > 0) {
      let newPrimaryMedication = orderInfo.value.extraMedications.splice(0, 1);
      orderInfo.value.medication = newPrimaryMedication[0].medication;
      orderInfo.value.expectedLengthOfTherapy = newPrimaryMedication[0].expectedLengthOfTherapy;
      orderInfo.value.quantityPer30Days = newPrimaryMedication[0].quantityPer30Days;
      orderInfo.value.packageSizeUnit = newPrimaryMedication[0].packageSizeUnit;
      orderInfo.value.dailyDosage = newPrimaryMedication[0].dailyDosage;
      orderInfo.value.nationalDrugCode = newPrimaryMedication[0].nationalDrugCode;
      orderInfo.value.treatmentStartDate = newPrimaryMedication[0].treatmentStartDate;
      orderInfo.value.frequency = newPrimaryMedication[0].frequency;
      orderInfo.value.daw = newPrimaryMedication[0].daw;
      // Falls back to "" rather than undefined: an extra medication stored before LAM-4951 has
      // no prescriptionNumber, and undefined would drop the key from the PUT body, stranding the
      // demoted medication's Rx number on the order (LAM-4951).
      orderInfo.value.prescriptionNumber = newPrimaryMedication[0].prescriptionNumber ?? "";
    } else {
      orderInfo.value.medication = "";
      orderInfo.value.expectedLengthOfTherapy = "";
      orderInfo.value.quantityPer30Days = "";
      orderInfo.value.dailyDosage = "";
      orderInfo.value.packageSizeUnit = "";
      orderInfo.value.nationalDrugCode = "";
      // Treatment start date is a DateField on the backend; null clears it cleanly,
      // "" would be rejected by save-order with 400 (LAM-3863).
      orderInfo.value.treatmentStartDate = null;
      orderInfo.value.frequency = "";
      orderInfo.value.daw = "No";
      orderInfo.value.prescriptionNumber = "";
    }
  }

  function updateExtraMedicationField(index, field, newValue) {
    orderInfo.value.extraMedications[index][field] = newValue;
  }

  // Additional medications queued for their OWN separate PA submission (LAM-4841) — distinct
  // from extraMedications above, which lists multiple drugs on ONE fax cover form/submission.
  // Each entry here becomes its own OrderInfo, sharing this order's patient/provider/insurance/
  // diagnosis/docs fields, via orderInfoService.createSubmissionsBatch at final submit time.
  // Capped to match CreateSubmissionsBatchRequest's server-side limit — otherwise the wizard
  // would let a user queue more than the backend accepts, only to have the whole batch (not
  // just the excess) rejected with a single generic 400 at final submit.
  function addAdditionalMedication() {
    if (orderInfo.value.draftAdditionalMedications.length >= MAX_ADDITIONAL_MEDICATIONS) return;
    orderInfo.value.draftAdditionalMedications.push({
      medication: "",
      expectedLengthOfTherapy: "",
      quantityPer30Days: "",
      packageSizeUnit: "",
      frequency: "",
      dailyDosage: "",
      nationalDrugCode: "",
      prescriptionNumber: "",
      treatmentStartDate: null,
      daw: "No",
    });
  }

  function removeAdditionalMedication(index) {
    orderInfo.value.draftAdditionalMedications.splice(index, 1);
  }

  function updateAdditionalMedicationField(index, field, newValue) {
    orderInfo.value.draftAdditionalMedications[index][field] = newValue;
  }

  // Validation functions

  /**
   * True for a non-empty string. Treats `undefined` the same as `null`/"" — a field a data
   * source never set at all (e.g. Brooks' import builder omits providerContactFax/providerTitle
   * entirely when the provider FK doesn't resolve, rather than sending null) is `undefined`
   * after camelization, not `null`; the naive `value !== null && value.trim()` pattern used
   * throughout this file assumed every required field would at least be present-but-null, and
   * threw `TypeError: Cannot read properties of undefined (reading 'trim')` for one that wasn't —
   * aborting the whole Create Submission handler before any request was ever sent.
   */
  function hasValue(value) {
    return typeof value === "string" && value.trim() !== "";
  }

  function addressValid(field) {
    const value = orderInfo.value[field];
    return hasValue(value) && value.length <= 35;
  }

  function validPatientInfo() {
    const isCmmImport = orderInfo.value.orderSubmission?.isCmmImport;
    const requiredFields = [
      "patientFirstName",
      "patientLastName",
      "patientDob",
      "patientGender",
      "patientAddress1",
      "patientCity",
      "patientState",
      "patientZip",
      "patientMemberId",
    ];

    if (!isCmmImport) {
      requiredFields.push("benefitType");
    }

    // A fax form picked on Create Submission (LAM-5197) routes the order to FAX regardless of
    // submissionMethod, and a fax never needs a BIN — the server (_create_submission) enforces it
    // only for CoverMyMeds routing, so the form must not demand it either.
    const faxFormSelected = !!orderInfo.value.faxSubmissionFormDetail;
    if (orderInfo.value.submissionMethod === "CoverMyMeds" && !isCmmImport && !faxFormSelected) {
      requiredFields.push("bin");
    }

    return (
      requiredFields.every((field) => hasValue(orderInfo.value[field])) &&
      addressValid("patientAddress1") &&
      isZipCodeValid(orderInfo.value.patientZip) &&
      isPhoneNumberValid(orderInfo.value.patientPhone) &&
      isDobValid(orderInfo.value.patientDob)
    );
  }

  function validProviderInfo() {
    const requiredFields = [
      "providerFirstName",
      "providerLastName",
      "providerContactPhone",
      "providerContactFax",
      "providerAddress1",
      "providerCity",
      "providerState",
      "providerZip",
      "providerNpiNumber",
    ];

    const baseValid =
      requiredFields.every((field) => hasValue(orderInfo.value[field])) &&
      isNPIValid(orderInfo.value.providerNpiNumber) &&
      addressValid("providerAddress1") &&
      isZipCodeValid(orderInfo.value.providerZip) &&
      isPhoneNumberValid(orderInfo.value.providerContactPhone) &&
      isPhoneNumberValid(orderInfo.value.providerContactFax);

    if (orderInfo.value.forwardResponsesToProviderByEmail) {
      return baseValid && isEmailValid(orderInfo.value.providerEmail);
    }
    return baseValid;
  }

  function validMedicationFields(fields) {
    const isCmmImport = orderInfo.value.orderSubmission?.isCmmImport;
    const requiredFields = ["medication"];

    const numericalFields = ["expectedLengthOfTherapy", "quantityPer30Days"];

    let ndcValidation = [];
    // Package size unit and NDC are CoverMyMeds inputs; a selected fax form (LAM-5197) routes the
    // order to FAX regardless of submissionMethod, so neither is required then — same rule as the
    // BIN in validPatientInfo and the asterisks in MedicalInformationForm.
    const faxFormSelected = !!orderInfo.value.faxSubmissionFormDetail;
    if (orderInfo.value.submissionMethod === "CoverMyMeds" && !faxFormSelected) {
      requiredFields.push("packageSizeUnit");
      if (!isCmmImport) {
        ndcValidation.push("nationalDrugCode");
      }
    }

    return (
      requiredFields.every((field) => hasValue(fields[field])) &&
      numericalFields.every((field) => hasValue(fields[field]) && !isNaN(fields[field])) &&
      ndcValidation.every((field) => {
        // Same rule as the NDC field's own validator: a dashed NDC is valid input that
        // gets normalized on save, so the step must not block on it. Writers that set
        // nationalDrugCode programmatically (autofill, intake prepopulation) skip the
        // field's @change handler, so the raw dashed value can still be sitting here.
        const value = fields[field];
        return hasValue(value) && isValidNdc(value);
      })
    );
  }

  function validMedicationInfo() {
    return validMedicationFields(orderInfo.value);
  }

  function validAdditionalMedicationsInfo() {
    return (
      orderInfo.value.draftAdditionalMedications.length <= MAX_ADDITIONAL_MEDICATIONS &&
      orderInfo.value.draftAdditionalMedications.every((medication) => validMedicationFields(medication))
    );
  }

  function validDiagnosisInfo() {
    const requiredFields = ["primaryDiagnosisIcd10Code", "primaryDiagnosisName"];
    return requiredFields.every((field) => hasValue(orderInfo.value[field]));
  }

  function validSupportingDocs(required = true) {
    if (!required) return true;
    const hasLinkedDoc = (docs) => Array.isArray(docs) && docs.some((doc) => doc?.links?.length > 0);
    return hasLinkedDoc(orderInfo.value.patientChart) || hasLinkedDoc(orderInfo.value.aiReferenceDocuments);
  }

  // Drop the items a cleared field left behind, so the answered count on the screen stays honest.
  // The save itself is scoped by the declared questions, not by what this list still holds.
  function dropUnansweredChecklistRows() {
    if (orderInfo.value.checklist && Array.isArray(orderInfo.value.checklist)) {
      orderInfo.value.checklist = orderInfo.value.checklist.filter(
        (item) =>
          item.options &&
          Array.isArray(item.options) &&
          item.options.length > 0 &&
          item.options.some((opt) => opt.id && opt.label)
      );
    }
  }

  // Save functions
  // markChartComplete: an explicit Save draft (not the start-autocomplete dialog's "Save Only"), which marks the
  // patient chart complete once it holds a document.
  async function savePartialOrderInfo({
    startAutocomplete = false,
    markChartComplete = false,
    autofillAppliedTaskId = null,
  } = {}) {
    // Every field this save writes onto the form itself, returned so a caller can tell them apart
    // from edits made while the save was in flight.
    const written = {};
    if (!orderInfo.value.orderKey) {
      const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
      orderInfo.value.orderKey = newOrder.orderKey;
      orderInfo.value.assignees = newOrder.assignees;
      orderInfo.value.assignedGroups = newOrder.assignedGroups;
      written.orderKey = newOrder.orderKey;
      written.assignees = newOrder.assignees;
      written.assignedGroups = newOrder.assignedGroups;
    }

    dropUnansweredChecklistRows();
    written.checklist = orderInfo.value.checklist;

    if (startAutocomplete) {
      orderInfo.value.formStep = "Review";
      written.formStep = "Review";
    }
    const payload = { ...orderInfo.value };
    if (startAutocomplete) payload.startAutocomplete = true;
    if (markChartComplete) payload.markChartComplete = true;
    if (autofillAppliedTaskId) payload.autofillAppliedTaskId = autofillAppliedTaskId;
    const saved = await orderInfoService.saveOrder(orderInfo.value.orderKey, payload, declaredPayerQuestionIds.value);
    if (saved?.editableSubmissionForm) {
      orderInfo.value.editableSubmissionForm = saved.editableSubmissionForm;
      written.editableSubmissionForm = saved.editableSubmissionForm;
    }
    return written;
  }

  return {
    // State
    formButtonClicked,
    declaredPayerQuestionIds,

    // Update functions
    updateChecklistSelection,
    updateDeclaredQuestions,
    updateBasicPlusChecklistSelection,
    updateSignature,
    updatePrimaryDiagnosis,
    updateOtherDiagnosis,
    addOtherDiagnosis,
    updateOrderInfoField,
    updateSupportingDocs,
    updateAiReferenceDocuments,
    isSessionUpload,
    addExtraMedication,
    removeExtraMedication,
    removePrimaryMedication,
    updateExtraMedicationField,
    addAdditionalMedication,
    removeAdditionalMedication,
    updateAdditionalMedicationField,

    // Validation functions
    addressValid,
    validPatientInfo,
    validProviderInfo,
    validMedicationInfo,
    validAdditionalMedicationsInfo,
    validDiagnosisInfo,
    validSupportingDocs,

    // Utility functions
    dropUnansweredChecklistRows,

    // Save functions
    savePartialOrderInfo,
  };
}
